import React, { useEffect, useRef } from 'react'

import { Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'

import type { AutocompleteProps } from '@material-ui/lab/Autocomplete'

import type { ConfigLayerOptions } from 'config/ConfigLayer'

const useStyles = makeStyles({
  groupLabel: {
    fontSize: '0.9em',
    fontWeight: 500,
    opacity: 0.5,
    marginLeft: 5,
    marginTop: 5
  },
  groupUl: {
    padding: 0
  }
})

export const useMountRef = () => {
  const ref = useRef(false)

  useEffect(() => {
    ref.current = true
    return () => { ref.current = false }
  }, [])

  return ref
}

const SearchLayers = (props: SearchLayers) => {
  const classes = useStyles(props)

  // Keep track of whether or not component is mounted.
  // This is to prevent memory leaks when async functions
  // try to access the component after it has been unmounted
  const mountRef = useMountRef()

  const { searchOptions, selectedOption, setSelectedOption } = props

  const autocompleteClasses = {
    groupLabel: classes.groupLabel,
    groupUl: classes.groupUl
  }

  return (
    <Autocomplete
      autoFocus={false}
      classes={autocompleteClasses}
      options={searchOptions}

      getOptionLabel={(option) => option.search?.description || ''}

      groupBy={options => options.search?.group || 'search'}

      onChange={(_event, selectedOption, reason) => {
        console.debug('SearchOptions - onchange (item selected)', reason, selectedOption)

        // If no option is selected, select the first
        if (!selectedOption) {
          selectedOption = searchOptions?.[0]
          console.debug('SearchOptions - no option selected, selecting first one', selectedOption)
        }

        if (!selectedOption) {
          console.debug('SearchOptions - no option selected nor first option found', selectedOption)
          return
        }

        if (mountRef.current) {
          setSelectedOption(selectedOption)
        }
      }}

      renderGroup={params => {
        // console.debug('SearchOptions - renderGroup', params)

        // Show divider if group has changed (and is not 'search')
        const divider = params.group !== 'search' ? <Divider /> : null
        const groupLabel = params.group !== 'search' ? <div className={classes.groupLabel}>{params.group}</div> : ''

        return (
          <div key={params.key}>
          {divider}
          {groupLabel}
            <li>
              <ul className={classes.groupUl}>{params.children}</ul>
            </li>
          </div>
        )
      }}

      renderInput={(params) => (
        <TextField
          {...params}
          margin="normal"
          placeholder="Select search option..."
          />
      )}

      value={selectedOption}

      getOptionSelected={(selectedOption, value: ConfigLayerOptions) =>
        selectedOption.titleid === value.titleid && selectedOption.search?.field === value.search?.field
      }
    />
  )
}

SearchLayers.defaultProps = {
  autoFocus: false,
  isWidget: false,
  placeholder: 'Start typing here...'
}

export default SearchLayers

interface SearchLayers extends Partial<AutocompleteProps<ConfigLayerOptions, boolean, boolean, boolean>> {
  searchOptions: ConfigLayerOptions[]
  selectedOption?: ConfigLayerOptions
  setSelectedOption: (value: ConfigLayerOptions) => void
}
